import { APP_LANGUAGES, LOCALES } from '@iac/contracts';
import type { AppKey, Locale } from '@iac/contracts';
import { useMutation } from '@tanstack/react-query';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, unwrap } from './api';
import { useAuth } from './auth';

const STORAGE_KEY = 'lastLocale';

function isLocale(value: unknown): value is Locale {
  return LOCALES.includes(value as Locale);
}

/** Storage can be blocked (private window, cleared site data) — never fatal. */
function readStored(): Locale | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return isLocale(value) ? value : null;
  } catch {
    return null;
  }
}

function writeStored(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Only the pre-login guess is lost.
  }
}

function browserLocale(): Locale | null {
  for (const tag of navigator.languages) {
    const prefix = tag.slice(0, 2).toLowerCase();
    if (isLocale(prefix)) return prefix;
  }
  return null;
}

/** The language before the session says otherwise. */
function initialLocale(): Locale {
  return readStored() ?? browserLocale() ?? 'en';
}

/**
 * Module scope, like the access token in `api.ts`: `toastError` runs from the
 * query cache, outside React, and still has to speak the screen's language.
 */
let effectiveLocale: Locale = initialLocale();

/** The language the screen renders in right now, for code outside React. */
export function currentLocale(): Locale {
  return effectiveLocale;
}

interface LocaleValue {
  /** What the screen renders in: the preference, unless the app lacks it. */
  locale: Locale;
  /** The user's chosen language, as stored. */
  preference: Locale;
  /** The app the user is inside, or null on shell pages. */
  appKey: AppKey | null;
  setPreference: (locale: Locale) => void;
  setAppKey: (appKey: AppKey | null) => void;
}

const LocaleContext = createContext<LocaleValue | null>(null);

/**
 * Owns the UI language. The session's stored value wins once known; a change
 * applies at once and is saved in the background, rolled back if the save
 * fails (the mutation cache toasts the error).
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const { session, setUser } = useAuth();
  const [preference, setPreferenceState] = useState<Locale>(initialLocale);
  const [appKey, setAppKey] = useState<AppKey | null>(null);

  // Adopt the stored value whenever the session brings a new one (sign-in,
  // `/me`, a save's response) — adjusted during render, not in an effect.
  const sessionLocale = session?.user.locale;
  const [adopted, setAdopted] = useState(sessionLocale);
  if (sessionLocale && sessionLocale !== adopted) {
    setAdopted(sessionLocale);
    setPreferenceState(sessionLocale);
  }

  // Remembered for the login page, which renders before any session exists.
  useEffect(() => writeStored(preference), [preference]);

  const save = useMutation({
    mutationFn: async (locale: Locale) =>
      unwrap(
        await api.auth.setLocale({ body: { locale } }),
        200,
        'Could not save the language.',
      ),
    onMutate: (locale) => {
      const previous = preference;
      setPreferenceState(locale);
      return { previous };
    },
    onError: (_error, _locale, context) => {
      if (context) setPreferenceState(context.previous);
    },
    onSuccess: (user) => setUser(user),
  });

  const locale = useMemo(() => {
    if (!appKey) return preference;
    const supported: readonly Locale[] = APP_LANGUAGES[appKey];
    return supported.includes(preference) ? preference : supported[0];
  }, [appKey, preference]);

  useEffect(() => {
    document.documentElement.lang = locale;
    effectiveLocale = locale;
  }, [locale]);

  const { mutate } = save;
  const value = useMemo<LocaleValue>(
    () => ({
      locale,
      preference,
      appKey,
      setPreference: (next) => mutate(next),
      setAppKey,
    }),
    [locale, preference, appKey, mutate],
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export function useLocale(): LocaleValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used inside LocaleProvider');
  return ctx;
}
